'use client';
import { type Brand } from '@curology/ui-components-web/brand';
import { Button, Typography } from '@curology/ui-components-web/react';
import { LockIcon } from '@curology/ui-components-web/react/Icon/icons/LockIcon';
import React, { useEffect, useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { useStripeClient } from '@/components/payments/hooks/useStripeClient';
import { PAYMENT_TYPES, PROCESSOR_NAMES } from '@/consts/payments';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { Loggingservice } from '@/services/loggingService/client';
import { PaymentMethodService } from '@/services/paymentsService/client';
import { editSessionBag } from '@/services/visitsService/client';
import { AnalyticsEvent, FlowTypes, trackEvent } from '@/utils/analytics';

import { useNavigation } from '../../../hooks/useNavigation';

// This component is primarily based off the code in apps/curology-sign-up/src/components/payments/components/registerCreditCardPaymentMethodForm.tsx.
export const CreditCardForm = ({
  brand,
  disableForms,
  enableForms,
  isSubmitting,
  offerId,
  uuid,
}: {
  brand: Brand;
  disableForms: () => void;
  enableForms: () => void;
  isSubmitting: boolean;
  offerId: number;
  uuid: string;
}) => {
  const stripeClient = useStripeClient();
  const [card, setCard] = useState<any>();
  const [isStripeInitialized, setIsStripeInitialized] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);

  const flashMessages = useFlashMessages();
  const { handleIfSessionExpired } = useSessionExpired(brand);
  const { onNext } = useNavigation();

  const stripeCcInputId = 'stripe-cc-input';

  const submitForm = async () => {
    card.update({ disabled: true });
    disableForms();

    try {
      const stripeToken = await stripeClient?.createToken(card);

      if (
        !stripeToken ||
        !stripeToken.token ||
        !stripeToken.token.card ||
        stripeToken.error
      ) {
        card.update({ disabled: false });
        enableForms();

        flashMessages.error({
          title: 'Sorry!',
          message:
            stripeToken?.error?.message ||
            'Error confirming the payment method. Please try again.',
        });

        return;
      }

      const data = {
        cardFundingType: stripeToken.token.card.funding,
        cardLastFour: stripeToken.token.card.last4,
        method: PAYMENT_TYPES.CREDIT_CARD,
        paymentNonce: stripeToken.token.id,
        vendor: PROCESSOR_NAMES.STRIPE,
      };

      await PaymentMethodService.storePaymentMethod({
        brand,
        data,
        onError: (message) => {
          card.update({ disabled: false });
          enableForms();

          flashMessages.error({
            title: 'Sorry!',
            message,
          });
        },
        onSuccess: () => {
          editSessionBag({
            brand,
            sessionBag: {
              hasAddedPaymentMethod: true,
            },
            uuid,
          }).catch((error) => {
            Loggingservice.sendLogsToDatadog(
              brand,
              '[Frontend] Payment Session Bag Update Failed',
              {
                error: error?.message || String(error),
                uuid,
                offerId,
              },
              'error',
            );
          });

          // Track analytics events
          trackEvent(AnalyticsEvent.CHECKOUT_CONTINUE_BUTTON_CLICKED, {
            card: 'payment',
            flow: FlowTypes.PDP_CHECKOUT,
          });

          trackEvent(AnalyticsEvent.CREDIT_CARD_CONTINUE_BUTTON_CLICKED, {
            flow: FlowTypes.PDP_CHECKOUT,
            offerId,
          });

          // Payment succeeded - proceed to next step
          onNext();
        },
      });
    } catch (e: any) {
      if (handleIfSessionExpired(e.response?.status)) return;
      flashMessages.error({
        title: 'Sorry!',
        message: 'A system error occurred. Please try again.',
      });
    }
  };

  useEffect(() => {
    const elements = stripeClient?.elements();

    if (elements && !isStripeInitialized) {
      setIsStripeInitialized(true);

      const card = elements.create('card', {
        hidePostalCode: true,
      });

      card.mount(`#${stripeCcInputId}`);

      card.addEventListener('change', (event) => {
        setErrorMessage(event?.error?.message || '');
        setCanSubmit(event?.complete || false);
      });

      setCard(card);
    }
  }, [stripeClient, isStripeInitialized]);

  return (
    <React.Fragment>
      <div className="cur-mb-4">
        <div className="cur-border cur-border-solid cur-border-subtle cur-p-[18px] cur-leading-[0] cur-text-center">
          <label className="cur-w-full" id="stripe-cc-input-container">
            <div id={stripeCcInputId} />
          </label>
        </div>
        {errorMessage && (
          <Typography className="cur-mt-1 cur-text-[#eb1c26]" size="meta">
            {errorMessage}
          </Typography>
        )}
      </div>
      <div className="cur-flex cur-items-center cur-mb-4">
        <LockIcon className="cur-mr-2" size="sm" />
        <Typography size="meta">Secured with 256-bit TLS encryption</Typography>
      </div>
      <div className="cur-mb-4">
        <Button
          className="cur-w-full"
          disabled={!canSubmit || isSubmitting}
          onClick={submitForm}
          type="button"
        >
          Next
        </Button>
      </div>
    </React.Fragment>
  );
};
